import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, X } from 'lucide-react';
import { api } from '../lib/api';
import { brandsKey, useBrands } from '../hooks/useMeta';
import { BRAND_LABELS } from '../lib/format';
import { TEMPLATE_PLACEHOLDERS } from '@shared/types';
import type { BrandDto, BrandId } from '@shared/types';

const BRAND_IDS: BrandId[] = ['buydubai', 'willroi'];

interface PreviewResult {
  subject: string;
  html: string;
}

export default function Templates() {
  const { data: brands = [], isLoading } = useBrands();
  const [brandId, setBrandId] = useState<BrandId>('buydubai');
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">Načítám…</div>;

  const brand = brands.find((b) => b.id === brandId);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold text-dark">Šablony</h1>
        <div className="flex gap-1 rounded-md border border-gray-200 p-1">
          {BRAND_IDS.map((id) => (
            <button
              key={id}
              onClick={() => setBrandId(id)}
              className={`rounded px-3 py-1 text-sm font-medium ${
                brandId === id ? 'bg-teal text-white' : 'text-gray-600'
              }`}
            >
              {brands.find((b) => b.id === id)?.name ?? BRAND_LABELS[id]}
            </button>
          ))}
        </div>
      </div>

      {!brand ? (
        <div className="card p-8 text-sm text-gray-500">
          Značka {BRAND_LABELS[brandId]} zatím není v databázi.
        </div>
      ) : (
        <>
          <TemplateSection
            key={`${brand.id}-confirm`}
            brand={brand}
            title="Potvrzení leadovi"
            description="E-mail, který dostane lead hned po odeslání formuláře."
            subjectField="confirmSubject"
            bodyField="confirmHtml"
            html
            onPreview={setPreview}
          />
          <TemplateSection
            key={`${brand.id}-broker`}
            brand={brand}
            title="E-mail makléři"
            description="Notifikace makléřům nastaveným u formuláře."
            subjectField="brokerNotifySubject"
            bodyField="brokerNotifyHtml"
            html
            onPreview={setPreview}
          />
          <TemplateSection
            key={`${brand.id}-wa-broker`}
            brand={brand}
            title="WhatsApp makléři"
            description="Prostý text odeslaný přes Whatchimp na telefony makléřů."
            bodyField="whatchimpBrokerTemplate"
          />
          <TemplateSection
            key={`${brand.id}-wa-lead`}
            brand={brand}
            title="WhatsApp leadovi"
            description="Prostý text odeslaný přes Whatchimp přímo leadovi."
            bodyField="whatchimpLeadTemplate"
          />
        </>
      )}

      <section className="card p-5">
        <h2 className="mb-2 font-heading text-lg font-semibold text-dark">
          Výjimky pro jednotlivé formuláře
        </h2>
        <p className="text-sm text-gray-600">
          Tady se nastavují texty pro celou značku. Pokud má konkrétní formulář mít vlastní předmět
          a text potvrzovacího e-mailu, edituje se to v sekci{' '}
          <span className="font-medium">Stránky a formuláře</span> u daného formuláře. Prázdný
          override znamená, že se použije šablona značky.
        </p>
      </section>

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

type SubjectField = 'confirmSubject' | 'brokerNotifySubject';
type BodyField =
  | 'confirmHtml'
  | 'brokerNotifyHtml'
  | 'whatchimpBrokerTemplate'
  | 'whatchimpLeadTemplate';

function TemplateSection({
  brand,
  title,
  description,
  subjectField,
  bodyField,
  html,
  onPreview,
}: {
  brand: BrandDto;
  title: string;
  description: string;
  subjectField?: SubjectField;
  bodyField: BodyField;
  html?: boolean;
  onPreview?: (preview: PreviewResult) => void;
}) {
  const qc = useQueryClient();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [subject, setSubject] = useState(subjectField ? brand[subjectField] : '');
  const [body, setBody] = useState(brand[bodyField]);

  const dirty =
    body !== brand[bodyField] || (subjectField ? subject !== brand[subjectField] : false);

  const save = useMutation({
    mutationFn: () => {
      // Posíláme jen změněná pole — PUT /api/brands/:id je parciální.
      const patch: Partial<BrandDto> = {};
      if (body !== brand[bodyField]) patch[bodyField] = body;
      if (subjectField && subject !== brand[subjectField]) patch[subjectField] = subject;
      return api.put<BrandDto>(`/brands/${brand.id}`, patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: brandsKey }),
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      api.post<PreviewResult>('/templates/preview', {
        html: body,
        subject,
        brandId: brand.id,
      }),
    onSuccess: (result) => onPreview?.(result),
  });

  /** Vloží placeholder na pozici kurzoru v textarea — rychlejší než opisovat závorky. */
  const insertPlaceholder = (placeholder: string) => {
    const el = bodyRef.current;
    const token = `{${placeholder}}`;
    if (!el) {
      setBody(body + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <section className="card space-y-3 p-5">
      <div>
        <h2 className="font-heading text-lg font-semibold text-dark">{title}</h2>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      {subjectField && (
        <label className="block">
          <span className="label">Předmět</span>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
      )}

      <label className="block">
        <span className="label">{html ? 'HTML obsah' : 'Text zprávy'}</span>
        <textarea
          ref={bodyRef}
          className="input font-mono text-xs"
          rows={html ? 14 : 6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500">Placeholdery:</span>
        {TEMPLATE_PLACEHOLDERS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => insertPlaceholder(p)}
            className="rounded-full border border-gray-200 px-2.5 py-0.5 font-mono text-xs text-gray-600 transition-colors hover:border-teal hover:bg-teal-light hover:text-teal"
          >
            {`{${p}}`}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          className="btn-primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Ukládám…' : 'Uložit'}
        </button>
        {html && onPreview && (
          <button
            className="btn-secondary"
            disabled={previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            <Eye size={16} /> {previewMutation.isPending ? 'Připravuji…' : 'Náhled'}
          </button>
        )}
      </div>

      {save.isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {(save.error as Error).message}
        </div>
      )}
      {save.isSuccess && !save.isPending && !dirty && (
        <div className="rounded-md border border-positive/40 bg-positive-bg p-3 text-sm text-positive">
          Uloženo. Nové leady už používají aktuální text.
        </div>
      )}
      {previewMutation.isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          Náhled se nepodařilo vygenerovat: {(previewMutation.error as Error).message}
        </div>
      )}
    </section>
  );
}

function PreviewModal({ preview, onClose }: { preview: PreviewResult; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center bg-dark/40 p-6 pt-16"
      onClick={onClose}
    >
      <div
        className="card flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <div className="label mb-0">Předmět</div>
            <div className="truncate font-medium text-dark">{preview.subject || '—'}</div>
          </div>
          <button className="btn-ghost ml-auto" onClick={onClose} aria-label="Zavřít">
            <X size={18} />
          </button>
        </div>
        <iframe
          title="Náhled e-mailu"
          className="h-[60vh] w-full bg-white"
          sandbox=""
          srcDoc={preview.html}
        />
      </div>
    </div>
  );
}
